import { z } from 'zod';
import { CodeList, CodelistsResponse, CodelistsResponseSchema, CodelistData, IsoCodelistsResponseSchema, ISOCodelistResponse } from './types';
import { Notify } from '@shared/ui-native/Notify';

async function getCodelistForProject(): Promise<CodeList | null> {
    const response: Response = await fetch(`/kuura/wtd/${kuura.getProjectUuid()}/2024/types/codelists`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
    });

    if (!response.ok) {
        console.log(`No codelist found for project: ${response.statusText}`);
        return null;
    }

    const data: unknown = await response.json();
    const parseResult: z.ZodSafeParseResult<ISOCodelistResponse> = IsoCodelistsResponseSchema.safeParse(data);

    if (!parseResult.success) {
        console.error('Failed to parse codelists response:', parseResult.error);
        throw new Error('Invalid codelists response format');
    }

    if (parseResult.data.data.length === 0) {
        return null;
    }

    return parseResult.data.data[0] || null;
}

async function getCodelists(): Promise<CodelistData[]> {
    const response: Response = await fetch('/kuura/wtd/2024/types/codelists', {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
    });

    if (!response.ok) {
        throw new Error(`Error fetching codelists: ${response.statusText}`);
    }
    const data: unknown = await response.json();

    const parseResult: z.ZodSafeParseResult<CodelistsResponse> = CodelistsResponseSchema.safeParse(data);

    if (!parseResult.success) {
        console.error('Failed to parse codelists response:', parseResult.error);
        throw new Error('Invalid codelists response format');
    }

    return parseResult.data;
}

async function createCodelist(codelist: CodeList): Promise<CodelistOperationResult> {
    const response: Response = await fetch('/kuura/wtd/2024/types/codelist', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(codelist),
    });

    if (!response.ok) {
        Notify.error(`Error creating codelist: ${response.statusText}`);
        return { success: false, message: `Error creating codelist: ${response.statusText}` };
    }

    return { success: true, message: 'Codelist created successfully.' };
}

async function getCodelistUsageCount(codelistUuid: string): Promise<number> {
    const response: Response = await fetch(`/kuura/wtd/2024/types/codelist/${codelistUuid}/usage`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
    });

    if (!response.ok) {
        throw new Error(`Error fetching codelist usage count: ${response.statusText}`);
    }

    const data: { usageCount: number } = { usageCount: parseInt(await response.text()) };
    return data.usageCount;
}

async function updateCodelist(codelistUuid: string, codelist: CodeList): Promise<CodelistOperationResult> {
    const response: Response = await fetch(`/kuura/wtd/2024/types/codelist/${codelistUuid}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(codelist),
    });

    if (!response.ok) {
        Notify.error(`Error updating codelist: ${response.statusText}`);
        return { success: false, message: `Error updating codelist: ${response.statusText}` };
    }

    Notify.success('Codelist updated successfully.');
    return { success: true, message: 'Codelist updated successfully.' };
}

async function deleteCodelist(codelistUuid: string): Promise<CodelistOperationResult> {
    const response: Response = await fetch(`/kuura/wtd/2024/types/codelist/${codelistUuid}`, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
    });

    if (!response.ok) {
        Notify.error(`Error deleting codelist: ${response.statusText}`);
        return { success: false, message: `Error deleting codelist: ${response.statusText}` };
    }

    Notify.success('Codelist deleted successfully.');
    return { success: true, message: 'Codelist deleted successfully.' };
}

export type CodelistOperationResult = {
    success: boolean;
    message: string;
};

export const CodelistClient = {
    getCodelists,
    createCodelist,
    getCodelistForProject,
    getCodelistUsageCount,
    updateCodelist,
    deleteCodelist,
};
